import Button from '@mui/material/Button';
import { AccordionSummary, AppBar, Autocomplete, FormControl, InputLabel } from '@mui/material';
import React, { ReactElement, useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Select , { SelectChangeEvent } from '@mui/material/Select';
import { useWorkspaceContext } from '../../../../context/workspaceContext';
import { uuid } from '../../../../utils/utils';
import EventSequenceService from '../../../../services/event-sequence.service';
import { toast } from 'react-toastify';
import { BasicLinkedOccurrence, OccurrenceSummary, PsfTemplate, PsfTemplateItem } from '../../../../@types/occurrence-dna';
import EventSequence from '../../../../@types/event-sequence';
import { CanvasActionType } from '../../../../@types/canvas-action-type';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import SpringDataPage from '../../../../@types/spring-data-page';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FilterIcon from '@mui/icons-material/FilterAlt';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { debounce } from '@mui/material/utils';
import ActionDropdown from '../../../form-components/ActionDropdown.component';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as CanvasUtils from '../../../../utils/canvas/node-utils';
import { Chip } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import Toolbar from '@mui/material/Toolbar';
import CloseIcon from '@mui/icons-material/HighlightOff';

interface Column {
    id: 'linked' | 'actions' | 'source' | 'name' | 'location' | 'summary'  | 'datetime' ;
    label: string;
    minWidth?: number;
    align?: 'right';
    format?: (value: number) => string;
}

const columns: readonly Column[] = [
    { id: 'linked', label: 'Linked', minWidth: 30 },
    { id: 'actions', label: 'Actions', minWidth: 100 },
    { id: 'source', label: 'Source', minWidth: 100 },
    { id: 'name', label: 'Name', minWidth: 200 },
    { id: 'location', label: 'Location', minWidth: 70 },
    { id: 'summary', label: 'Summary', minWidth: 200 },
    { id: 'datetime', label: 'DateTime', minWidth: 100 },
];

const occurrenceStatusList = [
    'NOT_SENTENCED', 'PARTIALLY_SENTENCED', 'FOR_REVIEW', 'COMPLETED', 'MODIFIED'
]

function OccurrenceTable(props: {
    eventSequence: EventSequence,
    selectedOccurrences: BasicLinkedOccurrence[],
    onSelectChanged: (occurrence: OccurrenceSummary) => void,
    getDna: (occurrenceId: number) => void,
    saveDna: (occurrenceId: number) => void
}): ReactElement {
    const { eventSequence, selectedOccurrences, onSelectChanged , getDna, saveDna} = props;
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [occurrences, setOccurrences] = React.useState<SpringDataPage<OccurrenceSummary> | null>(null);
    const [searchTerm, setSearchTerm] = React.useState<string | null>(null);
    const [isRunning, setIsRunning] = React.useState<boolean>(false);
    const uuidRef = React.useRef(uuid());

    const debounceFetch = React.useMemo(
        () =>
            debounce(
                (
                    request: { input: string },
                    callback: (results: SpringDataPage<OccurrenceSummary>) => void,
                ) => {
                    const fetchOccurrences = async (searchTerm: string): Promise<any> => {
                        try {
                            setIsRunning(true);
                            const myOccurrences: SpringDataPage<OccurrenceSummary> = await EventSequenceService.fetchOccurrences(eventSequence.group.id!, 0, rowsPerPage, searchTerm);
                            callback(myOccurrences);
                        }
                        catch (error: any) {
                            toast.error('Could not fetch occurrences to choose from - ' + error.message);
                        } finally {
                            setIsRunning(false);
                        }
                    }

                    fetchOccurrences(request.input);
                },
                400,
            ),
        [],
    );

    const occurrenceShortSummary = (occurrence: OccurrenceSummary): string => {
        let summaryComponents = [];
        if (occurrence.occurrenceSource === 'AVIATION_ACCIDENT') {
            let airportName = occurrence.rawReport.airportName ?? occurrence.rawReport.location;
            if (airportName !== undefined) {
                summaryComponents.push(airportName);
            }
            let phaseOfFlight = occurrence.rawReport.broadPhaseOfFlight
            if (phaseOfFlight !== undefined) {
                summaryComponents.push(phaseOfFlight);
            }
            let airCarrier = occurrence.rawReport.airCarrier;
            if (airCarrier !== undefined) {
                summaryComponents.push(airCarrier);
            }
        } else if (occurrence.occurrenceSource === 'USER_DEFINED') {
            summaryComponents.push(occurrence.rawReport.description);
        }
        return summaryComponents.join(' - ');
    }

    const handleChangePage = (event: unknown, newPage: number): void => {
        EventSequenceService.fetchOccurrences(eventSequence.group.id!, newPage, rowsPerPage, searchTerm).then((response) => {
            setPage(newPage);
            setOccurrences(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        });

    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setIsRunning(true);
        EventSequenceService.fetchOccurrences(eventSequence.group.id!, 0, +event.target.value, searchTerm).then((response) => {
            setRowsPerPage(+event.target.value);
            setPage(0);
            setOccurrences(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setIsRunning(false);
        });
    };

    const onSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(event.target.value);
    };

    // add debounceFetch to useEffect
    useEffect(() => {
        if (searchTerm) {
            debounceFetch({ input: searchTerm }, (results: SpringDataPage<OccurrenceSummary>) => {
                setOccurrences(results);
            });
        }
    }, [searchTerm, debounceFetch]);


    useEffect(() => {
        setIsRunning(true);
        EventSequenceService.fetchOccurrences(eventSequence.group.id!, page, rowsPerPage, null).then((response) => {
            setOccurrences(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setIsRunning(false);
        });
    }, [eventSequence]);

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' , mt: 1}}>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isRunning}> 
                <CircularProgress color='inherit' />
            </Backdrop>

            <Box sx={{ justifyContent: 'space-between' }}>
                <TextField
                    label='Filter Occurrences'
                    aria-label='search field'
                    type='text'
                    variant='outlined'
                    size='small'
                    id={`filter-box-${uuidRef.current}`}
                    onChange={onSearchTermChange}
                    sx={{
                        ml: 1, mr: 1, mt: 1,
                    }}

                    slotProps={{
                        input: {
                            'role': 'filterbox',
                            'tabIndex': 0,
                            'aria-labelledby': `filter-box-${uuidRef.current}`,
                            'aria-label': 'filter',
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton aria-label='filter icon' onClick={():void  => {}}>
                                        <FilterIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }
                    }}
                />

                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={occurrences ? occurrences.page.totalElements  : 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    style={{display: 'flex', justifyContent: 'start'}}
                />
            </Box>

            <TableContainer sx={{ maxHeight: 200 }}>
                <Table stickyHeader aria-label="sticky table" size='small'>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    style={{ minWidth: column.minWidth }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {occurrences?.content.map((row: OccurrenceSummary) => {
                            const isItemSelected = selectedOccurrences.findIndex((item) => item.id === row.id) !== -1;
                            return (
                                <TableRow
                                    key={row.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell>
                                        <Checkbox
                                            checked={isItemSelected}
                                            inputProps={{ 'aria-label': 'Select' }}
                                            onClick={(): void => onSelectChanged(row)}
                                            disableRipple
                                            disabled={CanvasUtils.canAddOrEditResource(eventSequence) ? false : true}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <ActionDropdown
                                            onClick={(): void => { }}
                                            actions1={[
                                                { name: 'Get DNA', action: (): void => getDna(row.id), disabled: !isItemSelected },
                                                { name: 'Save DNA', action: (): void => saveDna(row.id), disabled: (!isItemSelected || !CanvasUtils.canAddOrEditResource(eventSequence))},
                                                { name: 'Link DNA', action: (): void => onSelectChanged(row), disabled: (isItemSelected || !CanvasUtils.canAddOrEditResource(eventSequence)) },
                                                { name: 'Unlink DNA', action: (): void =>  onSelectChanged(row), disabled: (!isItemSelected || !CanvasUtils.canAddOrEditResource(eventSequence))},
                                            ]}
                                            actions2={[
                                                { name: 'View Report', action: (): void => {}, disabled: false },
                                            ]}
                                            label=''/>
                                    </TableCell>
                                    <TableCell>
                                        {row.occurrenceSource}
                                    </TableCell>
                                    <TableCell>
                                        {row.displayName}
                                    </TableCell>
                                    <TableCell>
                                        {row.location ? row.location.name : '---'}
                                    </TableCell>
                                    <TableCell>
                                        {row.rawReport ? occurrenceShortSummary(row) : '---'}
                                    </TableCell>
                                    <TableCell>
                                        {row.dateTime ? row.dateTime.split('T')[0] : '---'}
                                    </TableCell>

                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

export default function PanelDna(props: { eventSequence: EventSequence, isActive: boolean, onDnaToggle: (openDnaPanel: boolean | string[]) => void }): ReactElement {
    const { eventSequence, isActive, onDnaToggle } = props;
    
    const { currentCanvasAction, setCurrentCanvasAction } = useWorkspaceContext();
    const [linkedOccurrences, setLinkedOccurrences] = useState<BasicLinkedOccurrence[]>([]);
    const [currentLinkedOccurrence, setCurrentLinkedOccurrence] = useState<BasicLinkedOccurrence | null>(null);
    const [currentOccurrenceStatus, setCurrentOccurrenceStatus] = useState<string>(occurrenceStatusList[0]);
    const [esdPsfTemplate, setEsdPsfTemplate] = useState<PsfTemplate | null>(null);
    const [open, setOpen] = React.useState(false);
    const [expanded, setExpanded] = React.useState<string | false>(false);
    const [selectedPsfItems, setSelectedPsfItems] = React.useState<PsfTemplateItem[]>([]);
    const [selectedPsfOccurrenceReports, setSelectedPsfOccurrenceReports] = React.useState<BasicLinkedOccurrence[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    
    const uuidRef = React.useRef(uuid());

    const fetchPsfCounts = (): void => {
        if (!eventSequence) return;
        if (eventSequence.id === 0) return;
        let occurrenceIds = selectedPsfOccurrenceReports.map((item) => item.id);
        let psfIds = selectedPsfItems.map((item) => item.id);
        EventSequenceService.getPsfPresenceStat(eventSequence.id, occurrenceIds, psfIds).then((response) => {
            setCurrentCanvasAction({
                actionType: CanvasActionType.CANVAS_PSF_COUNTS,
                query: {
                    psfIds: psfIds,
                    occurrenceIds: occurrenceIds,
                    psfCounts: response
                }
            });
        }).catch((error: Error) => {
            toast.error(error.message);
        });
    }

    const saveDNA = (occurrenceId: number): void => {
        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_SAVE_ODNA, query: occurrenceId });
    }

    const getDNA = (occurrenceId: number): void => {
        setLoading(true);
        EventSequenceService.getLinkedOccurrenceDna(eventSequence.id, occurrenceId).then((response) => {
            if (response.events.length === 0 && response.faultTreeNodes.length === 0) {
                toast.info('No DNA found for this occurrence');
            }
            setCurrentLinkedOccurrence(linkedOccurrences.find((item) => item.id === occurrenceId) || null);
            setCurrentOccurrenceStatus(response.occurrenceStatus);
            setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_PULLED_ODNA, query: response });
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setLoading(false);
        });
    }

    const onOccurrenceTableSelectChanged = (occurrence: OccurrenceSummary): void => {
        let linked = linkedOccurrences.findIndex((item) => item.id === occurrence.id) !== -1;
        if (!linked) {
            EventSequenceService.postOccurrenceEventSequenceLink(eventSequence.id, occurrence.id, currentOccurrenceStatus).then(() => {
                setLinkedOccurrences([...linkedOccurrences, { id: occurrence.id, displayName: occurrence.displayName }]);
            }).catch((error: Error) => {
                toast.error(error.message);
            });
        } else {
            EventSequenceService.deleteOccurrenceLinkedToEventSequence(eventSequence.id, occurrence.id).then(() => {
                setLinkedOccurrences(linkedOccurrences.filter((item) => item.id !== occurrence.id));
                if (currentLinkedOccurrence && currentLinkedOccurrence.id === occurrence.id) {
                    setCurrentLinkedOccurrence(null);
                }
            }).catch((error: Error) => {
                toast.error(error.message);
            });
        }

    }


    useEffect(() => {
        if (isActive && currentCanvasAction !== null && currentCanvasAction.actionType !== CanvasActionType.UNKNOWN) {
            switch (currentCanvasAction.actionType) {
                case CanvasActionType.CANVAS_TOGGLE_ODNA:
                    setTimeout(() => onDnaToggle(!open));
                    setOpen(!open);
                    if (open) {
                        setExpanded(false);
                        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_PULLED_ODNA, query: null });
                    }
                    break;
                default:
                    break;
            }
            setTimeout(() => {
                setCurrentCanvasAction({ actionType: CanvasActionType.UNKNOWN });
            }, 100);

        }

    }, [currentCanvasAction, isActive]);

    useEffect(() => {
        if (!eventSequence || !eventSequence.events || !open) return;
        setExpanded(false);
        setCurrentLinkedOccurrence(null);
        let linkedOccurrencePromise = EventSequenceService.getLinkedOccurrences(eventSequence.id);
        let esdPsfTemplatePromise = EventSequenceService.getPsfTemplate(eventSequence.id);
        setLoading(true);
        Promise.all([linkedOccurrencePromise, esdPsfTemplatePromise]).then((responses) => {
            setLinkedOccurrences(responses[0]);
            setEsdPsfTemplate(responses[1]);
            setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_PSF_TEMPLATE, query: responses[1] });
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setLoading(false);
        });
        
    }, [open]);

    useEffect(() => {
        if (expanded === 'psf-selection') {
            fetchPsfCounts();
        }
    }, [selectedPsfItems, selectedPsfOccurrenceReports, expanded]);

    const onLinkedOccurrenceChange = (event: SelectChangeEvent): void => {
        event.stopPropagation();
        event.preventDefault();
        let id = parseInt(event.target.value);
        getDNA(id);
    };

    const onLinkedOccurrenceStatusChange = (event: SelectChangeEvent): void => {
        setCurrentOccurrenceStatus(event.target.value);
        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_STATUS_CHANGE_ODNA, query: event.target.value });
    };

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean): void => {
        setExpanded(isExpanded ? panel : false);
        if (panel === 'psf-selection') {
            if (isExpanded) {
                fetchPsfCounts();
            } else {
                setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_CLEAR_PSF, query: null });
            }
        }
    };

    if (open === false) {
        return <></>;
    }


    return loading ?
        <Stack spacing={1}>
            <Skeleton variant='rectangular' width='100%' height={40} animation="wave"/>
            <Skeleton variant='rectangular' width='100%' height={80} animation="wave"/>
            <Skeleton variant='rectangular' width='100%' height={80} animation="wave"/>
        </Stack> : (
            <Paper sx={{ height: '100%', overflow: 'auto' }}>
                <Box sx={{flexGrow: 1}}>
                    <AppBar position="static" color="primary">
                        <Toolbar>
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                                Occurrence DNA Panel
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />

                            <IconButton
                                color="inherit"
                                component='button'
                                edge='end'
                                onClick={(): void => setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_TOGGLE_ODNA })}>
                                <CloseIcon sx={{width: 25, height: 25}}/>
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                </Box>
                <Stack direction="column" spacing={2} justifyContent='start' sx={{m: 1}}>
                    <FormControl sx={{minWidth: 270}}>
                        <InputLabel tabIndex={-1} id={`linked-occurrence-label-${uuidRef.current}`} htmlFor={`linked-occurrence-list-${uuidRef.current}`}>Linked Occurrence</InputLabel>
                        <Select
                            labelId={`linked-occurrence-label-${uuidRef.current}`}
                            id={`linked-occurrence-list-${uuidRef.current}`}
                            value={currentLinkedOccurrence ? currentLinkedOccurrence.id.toString() : ''}
                            label="Linked Occurrence"
                            onChange={onLinkedOccurrenceChange}
                            size='small'

                            inputProps={{
                                'role': 'textbox',
                                'tabIndex': 0,
                                'id': `linked-occurrence-list-${uuidRef.current}`,
                            }}
                            MenuProps={
                                {
                                    PaperProps: {
                                        style: {
                                            maxHeight: 230,
                                        }
                                    }
                                }
                            }
                        >
                            {linkedOccurrences.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{item.displayName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Stack direction="row" spacing={2} justifyContent='start' sx={{ m: 1 }}>
                        {currentLinkedOccurrence && <FormControl size='small' sx={{minWidth: 200}}>
                            <InputLabel tabIndex={-1} id={`occurrence-status-label-${uuidRef.current}`} htmlFor={`occurrence-status-list-${uuidRef.current}`}>Occurrence Status</InputLabel>
                            <Select
                                labelId={`occurrence-status-label-${uuidRef.current}`}
                                id={`occurrence-status-list-${uuidRef.current}`}
                                value={currentOccurrenceStatus}
                                label="Linked Occurrence"
                                onChange={onLinkedOccurrenceStatusChange}
                                inputProps={{
                                    'role': 'textbox',
                                    'tabIndex': 0,
                                    'id': `occurrence-status-list-${uuidRef.current}`,
                                }}
                            >
                                {occurrenceStatusList.map((status) => (
                                    <MenuItem key={status} value={status}>{status}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        }
                    
                        {currentLinkedOccurrence && <Button variant="contained" color="primary" size='small' onClick={(): void => getDNA(currentLinkedOccurrence.id)}>Get DNA</Button>}
                        {currentLinkedOccurrence && <Button variant="contained" color="primary" size='small' onClick={(): void => setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_CLEAR_ODNA})}>Clear DNA</Button>}
                    
                    </Stack>
                </Stack>
                <Stack direction="column" spacing={2} justifyContent='start' sx={{ m: 1 }}>
                    <Accordion disableGutters expanded={expanded === 'occurrence-management'} onChange={handleAccordionChange('occurrence-management')} disabled={!CanvasUtils.canAddOrEditResource(eventSequence)}>
                        <AccordionSummary
                            aria-controls="Statistics-content"
                            id={`odna-header-${uuidRef.current}`}
                            expandIcon={<ExpandMoreIcon />}
                        >
                            <Typography variant="h6" component="div" color='primary' sx={{ mr: 2 , mt: 0.5}}>Occurrences Management</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                    
                            <OccurrenceTable eventSequence={eventSequence}
                                selectedOccurrences={linkedOccurrences}
                                onSelectChanged={onOccurrenceTableSelectChanged}
                                getDna={getDNA}
                                saveDna={saveDNA}
                            />

                        </AccordionDetails>
                    </Accordion>
                    <Accordion disableGutters expanded={expanded === 'psf-selection'} onChange={handleAccordionChange('psf-selection')}>
                        <AccordionSummary
                            aria-controls="PSF-content"
                            id={`panel1a-header-${uuidRef.current}`}
                            expandIcon={<ExpandMoreIcon />}
                        >
                            <Typography variant="h6" component="div" color='primary'>Performance Shaping Factors</Typography>
                        </AccordionSummary>
                        <AccordionDetails >
                            <Autocomplete
                                options={esdPsfTemplate && esdPsfTemplate.psfTemplateItems ? esdPsfTemplate.psfTemplateItems.sort((a,b) => a.category.localeCompare(b.category)) : []}
                                getOptionLabel={(psfItem: PsfTemplateItem): string => psfItem.code ? `${psfItem.name} - ${psfItem.code}` : psfItem.name}
                                renderOption={(props, option: PsfTemplateItem): ReactElement => (
                                    <li {...props} key={option.id}>
                                        {option.code ? `${option.name} - ${option.code}` : option.name}
                                    </li>
                                )}
                                renderTags={(tagValue: PsfTemplateItem[], getTagProps): any => {
                                    return tagValue.map((option, index) => (
                                        <Chip
                                            label={option.code ? `${option.name} - ${option.code}` : option.name}
                                            {...getTagProps({ index })} key={option.id}
                                        />
                                    ))
                                }}
                                groupBy={(option: PsfTemplateItem): string => option.category}
                                renderInput={(params): any => <TextField {...params} label="PSF Code"/>}
                                onChange={(event: any, newValue: PsfTemplateItem[]): void => {
                                    setSelectedPsfItems(newValue);
                                }}
                                limitTags={6}
                                multiple
                                filterSelectedOptions
                            />
                            <Autocomplete
                                options={linkedOccurrences}
                                getOptionLabel={(option): string => option.displayName}
                                renderInput={(params): any => <TextField {...params} label="Occurrence Reports" />}
                                renderOption={(props, option: BasicLinkedOccurrence): ReactElement => (
                                    <li {...props} key={option.id}>
                                        {option.displayName}
                                    </li>
                                )}
                                renderTags={(tagValue:BasicLinkedOccurrence[], getTagProps): any => {
                                    return tagValue.map((option, index) => (
                                        <Chip
                                            label={option.displayName}
                                            {...getTagProps({ index })} key={option.id}
                                        />
                                    ))
                                }}
                                onChange={(event: any, newValue: BasicLinkedOccurrence[]): void => {
                                    setSelectedPsfOccurrenceReports(newValue);
                                }}
                                limitTags={6}                            
                                multiple
                                filterSelectedOptions
                                disableCloseOnSelect
                                sx={{ mt: 2 }}
                            />
                        </AccordionDetails>
                    </Accordion>

                </Stack>

            </Paper>
        );
}
